import z from "zod";

const userAction = z.object({
	date: z.coerce.date(),
	user: z.string(),
});

const metadata = z.object({
	created: userAction,
	updated: userAction.optional(),
});

const completed = z
	.object({
		labels: userAction.nullable(),
		shipping: userAction.nullable(),
	})
	.optional();

const manifest = z.object({
	fillings: z.record(z.string(), z.number()).optional(),
	bases: z
		.record(
			z.string(),
			z.object({
				quantity: z.number(),
				addons: z.record(z.string(), z.number()),
			}),
		)
		.optional(),
	// Custom-builder piñatas (CUSTOM-* skus), keyed by collection-item id so
	// workflow-step retries overwrite instead of duplicating. Each entry is
	// the per-piñata build sheet; these items are ALSO counted in
	// fillings/bases above (the aggregates stay the full day's pull list).
	custom: z
		.record(
			z.string(),
			z.object({
				number: z.string(),
				body: z.string(),
				fillings: z.array(z.string()),
				addons: z.array(z.string()),
			}),
		)
		.optional(),
});

export type Manifest = z.infer<typeof manifest>;

const address = z.object({
	street1: z.string(),
	street2: z.string().nullable(),
	city: z.string(),
	state: z.string(),
	postalCode: z.string(),
	country: z.string(),
	company: z.string().nullable(),
	firstName: z.string(),
	lastName: z.string(),
});

export const collectionItem = z.object({
	external: z.boolean(),
	id: z.string(),
	number: z.string(),
	createdAt: z.coerce.date().nullish(),
	externalId: z
		.object({
			orderId: z.string(),
			itemId: z.string(),
			fulfillmentOrderId: z.string(),
		})
		.nullish(),
	recipient: address,
	message: z.string().nullable(),
	addons: z.array(z.string()),
	product: z.object({
		quantity: z.number(),
		sku: z.string(),
		emoji: z.string().nullable(),
		body: z.string(),
		additions: z.array(z.string()),
		filling: z.union([z.string().nullish(), z.array(z.string())]).transform((val) => {
			if (typeof val === "string") return val ? [val] : [];
			if (Array.isArray(val)) return val;
			return [];
		}),
	}),
	graphics: z.object({
		outside: z.string().nullable(),
		inside: z.string().nullable(),
	}),
	fulfillAt: z.coerce.date().nullish(),
	fulfillBy: z.coerce.date().nullish(),
});
export type CollectionItem = z.infer<typeof collectionItem>;

const collectionBase = z.object({
	lifecycle: z.enum(["rolling", "scheduled"]),
	fulfillAt: z.coerce.date().nullable(),
	fulfillBy: z.coerce.date().nullable(),
	completed,
	name: z.string(),
	orders: z.number().multipleOf(1).optional(),
	manifest,
	metadata,
});

export const rollingCollection = collectionBase.extend({
	lifecycle: z.literal("rolling"),
});

export type RollingCollection = z.infer<typeof rollingCollection>;

export const scheduledCollection = collectionBase.extend({
	lifecycle: z.literal("scheduled"),
	draft: z.literal(false),
	client: z.string().min(1, "Client name is required"),
});

export type ScheduledCollection = z.infer<typeof scheduledCollection>;

export const draftCollection = scheduledCollection
	.extend({
		draft: z.literal(true),
	})
	.partial({
		name: true,
		fulfillAt: true,
		fulfillBy: true,
		orders: true,
		manifest: true,
		completed: true,
	});

export type DraftCollection = z.infer<typeof draftCollection>;
export const draftCollectionInput = draftCollection.omit({
	lifecycle: true,
	draft: true,
	metadata: true,
});

const nullableDateInput = z.union([z.date(), z.string(), z.null()]).optional();

export const collectionPatchInputSchema = z
	.object({
		name: scheduledCollection.shape.name.optional(),
		client: scheduledCollection.shape.client.optional(),
		fulfillAt: nullableDateInput,
		fulfillBy: nullableDateInput,
	})
	.refine(
		(input) =>
			input.name !== undefined ||
			input.client !== undefined ||
			input.fulfillAt !== undefined ||
			input.fulfillBy !== undefined,
		{
			message: "At least one collection field is required",
		},
	);

export const collectionItemUpdateInputSchema = collectionItem
	.pick({
		number: true,
		recipient: true,
		message: true,
		addons: true,
		product: true,
		graphics: true,
	})
	.partial();

export const collectionItemMessageInputSchema = z.object({
	message: z.string(),
});

export type DraftCollectionInput = z.infer<typeof draftCollectionInput>;
export type CollectionCreateInput = DraftCollectionInput;
export type CollectionUpdateInput = z.infer<typeof collectionPatchInputSchema>;
export type CollectionItemUpdateInput = z.infer<typeof collectionItemUpdateInputSchema>;
export type CollectionItemMessageInput = z.infer<typeof collectionItemMessageInputSchema>;

export const collectionSchema = z.union([
	rollingCollection,
	z.discriminatedUnion("draft", [scheduledCollection, draftCollection]),
]);

export type Collection = z.infer<typeof collectionSchema>;

export const apiCollectionSchema = z.union([
	rollingCollection.extend({
		id: z.string(),
		items: z.array(collectionItem).optional(),
	}),
	z.discriminatedUnion("draft", [
		scheduledCollection.extend({
			id: z.string(),
			items: z.array(collectionItem).optional(),
		}),
		draftCollection.extend({
			id: z.string(),
			items: z.array(collectionItem).optional(),
		}),
	]),
]);

export type ApiCollection = z.infer<typeof apiCollectionSchema>;

export const apiCollectionItemSchema = collectionItem.extend({
	id: z.string(),
});

export type ApiCollectionItem = z.infer<typeof apiCollectionItemSchema>;
